import React, { useMemo } from "react";
import type { ApiDate } from "../lib/types";

const DAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];

interface MonthGridProps {
  // Calendar view month (independent of the selection); navigation
  // here moves it without changing what's selected.
  viewMonth: ApiDate;
  onViewMonthChange: (next: ApiDate) => void;
  // Selected range. For single-day events start === end.
  selectionStart: ApiDate;
  selectionEnd: ApiDate;
  onSelectDate: (clicked: ApiDate) => void;
}

interface Cell {
  date: ApiDate;
  inMonth: boolean;
  isToday: boolean;
  isStart: boolean;
  isEnd: boolean;
  inRange: boolean;
}

function isSame(a: ApiDate, b: ApiDate): boolean {
  return a.year === b.year && a.month === b.month && a.day === b.day;
}

function cmp(a: ApiDate, b: ApiDate): number {
  if (a.year !== b.year) return a.year - b.year;
  if (a.month !== b.month) return a.month - b.month;
  return a.day - b.day;
}

function buildGrid(view: ApiDate, start: ApiDate, end: ApiDate): Cell[] {
  const firstOfMonth = new Date(view.year, view.month - 1, 1);
  const gridStart = new Date(firstOfMonth);
  gridStart.setDate(gridStart.getDate() - gridStart.getDay());

  const now = new Date();
  const today: ApiDate = {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    day: now.getDate(),
  };

  const cells: Cell[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    const apiDate: ApiDate = {
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      day: d.getDate(),
    };
    cells.push({
      date: apiDate,
      inMonth: d.getMonth() === firstOfMonth.getMonth(),
      isToday: isSame(apiDate, today),
      isStart: isSame(apiDate, start),
      isEnd: isSame(apiDate, end),
      inRange: cmp(apiDate, start) >= 0 && cmp(apiDate, end) <= 0,
    });
  }
  return cells;
}

export function MonthGrid({
  viewMonth,
  onViewMonthChange,
  selectionStart,
  selectionEnd,
  onSelectDate,
}: MonthGridProps) {
  const cells = useMemo(
    () => buildGrid(viewMonth, selectionStart, selectionEnd),
    [viewMonth, selectionStart, selectionEnd],
  );

  const monthLabel = useMemo(() => {
    const d = new Date(viewMonth.year, viewMonth.month - 1, 1);
    return new Intl.DateTimeFormat(undefined, {
      month: "long",
      year: "numeric",
    }).format(d);
  }, [viewMonth]);

  const shift = (delta: number) => {
    const next = new Date(viewMonth.year, viewMonth.month - 1 + delta, 1);
    onViewMonthChange({
      year: next.getFullYear(),
      month: next.getMonth() + 1,
      day: 1,
    });
  };

  return (
    <div className="dpicker-grid">
      <header className="dpicker-grid-header">
        <button
          type="button"
          className="btn-icon"
          onClick={() => shift(-1)}
          aria-label="Previous month"
        >
          &lsaquo;
        </button>
        <span>{monthLabel}</span>
        <button
          type="button"
          className="btn-icon"
          onClick={() => shift(1)}
          aria-label="Next month"
        >
          &rsaquo;
        </button>
      </header>
      <div className="dpicker-grid-body">
        {DAY_LABELS.map((d, i) => (
          <span key={i} className="dpicker-day-label">
            {d}
          </span>
        ))}
        {cells.map((cell) => {
          const isRangeEdge = cell.isStart || cell.isEnd;
          const classes = [
            "dpicker-cell",
            cell.inMonth ? "" : "out-of-month",
            cell.isToday ? "today" : "",
            cell.inRange ? "in-range" : "",
            isRangeEdge ? "range-edge" : "",
            cell.isStart ? "range-start" : "",
            cell.isEnd ? "range-end" : "",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <button
              key={`${cell.date.year}-${cell.date.month}-${cell.date.day}`}
              type="button"
              className={classes}
              onClick={() => onSelectDate(cell.date)}
            >
              {cell.date.day}
            </button>
          );
        })}
      </div>
    </div>
  );
}
